<template>
  <div v-safeBottom class="protocol">
    <!-- :right-icon="hasHistoryIcon ? 'nav_history' : ''"
      @right-click="customRight" -->
    <NavBar :custom-back="customBack" />
    <client-only v-if="!noData">
      <p class="title">{{ protocolName }}</p>
      <BasicPdf v-if="isPdf" class="pdf-wrap" :url="pdfUrl" />
      <article
        v-else
        :class="['protocol-content', hasBtnWrap && 'pb-96']"
        @click="richTextTap"
        v-html="replaceHtmlALink(content, systemStore.isGWM)"
      />
      <div v-if="hasBtnWrap" class="btn-div" :class="{ 'pdf-div': isPdf }">
        <div class="btn-wrap">
          <button v-if="isPdf" class="btn activate" type="button" @click="downLoadPdf">
            <span>
              {{ systemStore.isAndroid ? $t('oc_down_pdf') : $t('base_view_all') }}
            </span>
          </button>
          <button
            v-if="isTelematic"
            type="button"
            :disabled="isLoading || !!countDownDes"
            :class="[
              'btn',
              isActive ? 'activate' : 'deactivate',
              !!countDownDes && 'count-down-disabled',
            ]"
            @click="telematicClick"
          >
            <BasicLoading v-if="isLoading" :white="!isActive" class="loading" />
            <span v-if="!(isLoading && isPdf)"> {{ telematicBtnText }}{{ countDownDes }} </span>
          </button>
        </div>
        <BasicBottomSafeArea />
      </div>
    </client-only>
    <BasicNoContent v-else class="pt-120" />
    <BasicDialog
      ref="dialogRef"
      :title="dialogTitle"
      :content="dialogContent"
      :confirm-text="dialogConfirm"
      :show-cancel="true"
      @confirm-event="revocationFn"
    />
  </div>
</template>
<script lang="ts" setup>
import { getProtocol, queryUserProtocolRecord, IHistoryList } from '~~/api/agreement'
import NavBar from '~~/components/nav/NavBar.vue'
import { debounce, replaceHtmlALink } from '~~/utils'
import { PROTOCOL_TYPE, SIGN_STATE } from '~~/enum'
import useSystemStore from '~~/store/system'
import useUserStore from '~~/store/user'
import { showPageLoading, hidePageLoading } from '~~/components/toast/index'

/**
 * @param {string} type // 协议类型. 1-用户协议，2-隐私协议，3-注销协议，4-试驾协议 ，5-订购协议，7-预售协议，11-互联服务协议 9-活动协议
 * @param {string} vehicleType // 车型（车联服务协议时必传）
 * @param {string} signAgreementState // 协议签署状态(APP传入)： 1-待签署, 2-已签署, 3-去更新
 * @param {string} flutter // 是否为flutter： 0-否, 1-是
 * @param {string} vin // 车辆vin码
 *
 */
const { $bt } = useNuxtApp()
const { t: $t } = useI18n()
const router = useRouter()
const query = useRoute().query as Record<string, string>
const type = Number(query.type) || 1
const { fromList = '', vehicleType = '', signAgreementState = '', flutter = '0', vin = '' } = query

const protocolName = ref('')
const isPdf = ref(false)
const pdfUrl = ref('')
const content = ref('')
const noData = ref(false)
const isLoading = ref(false)
const hasHistory = ref(false)
const userStore = useUserStore()
const systemStore = useSystemStore()

// 是否有查看历史协议按钮
const hasHistoryIcon = computed(() => {
  return (
    flutter === '0' &&
    [PROTOCOL_TYPE.PRIVACY, PROTOCOL_TYPE.TELEMATIC].includes(type) &&
    hasHistory.value
  )
})
// 是否展示车联协议签署按钮
const isTelematic = computed(() => type === PROTOCOL_TYPE.TELEMATIC)
// 是否有按钮
const hasBtnWrap = computed(() => systemStore.isGWM && (isPdf.value || isTelematic.value))
// 车联协议签署状态
const isActive = computed(() => isTelematic.value && signAgreementState === SIGN_STATE.UNSIGNED)
// 车联协议按钮
const telematicBtnText = computed(() => {
  if (isLoading.value) {
    return $t('oc_loading')
  } else {
    if (signAgreementState === SIGN_STATE.SIGNED) {
      return $t('ve_to_sign')
    }
    if (signAgreementState === SIGN_STATE.UNSIGNED) {
      return $t('oc_withdraw')
    }
    if (signAgreementState === SIGN_STATE.UPDATE) {
      return $t('oc_update')
    }
    return $t('ve_to_sign')
  }
})

onMounted(async () => {
  showPageLoading()
  $bt.call.refreshNav({ fullScreen: true })
  $bt.call.isBackIntercept({ isCanBack: true })
  $bt.call.enableFullScreenBack({ enable: false })
  // 侧滑返回页面方法控制
  $bt.register('backIntercept', () => {
    customBack()
    return false
  })
  await getProtocolFn(type)
  // getUserProtocolRecord()
  hidePageLoading()
  if (isTelematic.value && !isActive.value) {
    startCountDown()
  }
})
onUnmounted(() => stopCountDown())

// button enable count down
const countDown = ref(10)
const countDownDes = computed(() => {
  if (!isActive.value) {
    return countDown.value >= 0 ? `（${countDown.value}s）` : ''
  }
})
const countDownTimer = ref<NodeJS.Timeout>()
function startCountDown() {
  countDownTimer.value = setInterval(() => {
    if (countDown.value < 0) {
      stopCountDown()
    } else {
      countDown.value--
    }
  }, 1000)
}
function stopCountDown() {
  clearInterval(countDownTimer.value)
}

// 获取协议内容
async function getProtocolFn(type: number) {
  try {
    const { code, data } = await getProtocol({
      protocolType: type,
      country: userStore.country,
      vehicleType: (isTelematic.value && vehicleType) || undefined,
    })
    if (code === '000000') {
      protocolName.value = data?.protocolName ?? ''
      content.value = data.content
      isPdf.value = +data.contentType === 3
      if (isPdf.value) {
        pdfUrl.value = data.url ?? ''
      }
    }
    if (isPdf.value) {
      noData.value = !pdfUrl.value
    } else {
      noData.value = !content.value
    }
  } catch (error) {
    noData.value = false
    console.error(error)
  }
}

const getUserProtocolRecord = async () => {
  if (!userStore.accessToken) return
  const params: IHistoryList = {
    pageIndex: 1,
    pageSize: 10,
    protocolType: type,
    vin,
  }
  try {
    const { code, data } = await queryUserProtocolRecord(params)
    if (code === '000000') {
      hasHistory.value = !!data?.list.length
    }
  } catch (error) {
    console.log('获取协议列表失败')
  }
}

// 撤销协议
function revocationFn() {
  isLoading.value = true
  if (type === PROTOCOL_TYPE.TELEMATIC) {
    telematicConfirmFn()
  }
}

const dialogRef = ref()
const dialogTitle = ref('')
const dialogContent = ref('')
const dialogConfirm = ref('')

// 车联协议按钮触发事件
function telematicClick() {
  if (signAgreementState === SIGN_STATE.SIGNED) {
    isLoading.value = true
    telematicConfirmFn()
  } else if (signAgreementState === SIGN_STATE.UPDATE) {
    isLoading.value = true
    telematicConfirmFn()
  } else {
    dialogTitle.value = $t('oc_conform_to_withdraw')
    dialogContent.value = $t('oc_t_service_withdraw')
    dialogConfirm.value = $t('oc_withdraw')
    dialogRef.value?.show?.()
  }
}

function telematicConfirmFn() {
  $bt.call?.clickTServiceAgreement({}, (val: any) => {
    isLoading.value = false
  })
  dialogRef.value?.close?.()
}

function downLoadPdf() {
  if (systemStore.isAndroid) {
    $bt.call.checkFileExist({
      fileURL: pdfUrl.value,
    })
  } else {
    $bt.call.pushViewController({
      page: pdfUrl.value,
    })
  }
}

const richTextTap = debounce((e: any) => {
  const urlEle = e.target
  if (`${e.target.tagName}`.toLocaleUpperCase() === 'A') {
    e.preventDefault()
    const dataUrl = urlEle?.getAttribute('data-url')
    const httpRe = /^(http|https):\/\/\S*/
    // 只有http和https的协议才打开一个新的webview
    if (httpRe.test(dataUrl)) {
      $bt.call.pushViewController({
        page: dataUrl,
      })
    } else {
      window.location.href = dataUrl
    }
  }
}, 1000)

function customBack() {
  if (fromList) {
    router.go(-1)
  } else {
    $bt.call.close()
  }
}

function customRight() {
  router.push({
    path: '/protocol/historyList',
    query: { ...query },
  })
}
</script>
<style lang="less">
.protocol-content {
  img {
    max-width: 100%;
  }
}
</style>
<style lang="less" scoped>
.protocol-content {
  padding: 0 16px 96px;
  word-wrap: break-word;
  font-size: 16px;
}
.pt-120 {
  padding-top: 120px;
}
.pb-96 {
  padding-bottom: 96px;
}
.title {
  font-size: 24px;
  color: @Text50;
  line-height: 30px;
  padding: 10px 16px 16px;
}
.pdf-wrap {
  padding-bottom: 76px;
}
.btn-div {
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100vw;
  padding: 16px;
  background-color: @white;
  border-top: 1px solid @Line50;
  .btn-wrap {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.btn {
  width: 343px;
  height: 44px;
  line-height: 44px;
  text-align: center;
  font-size: 16px;
  border-radius: 4px;
  font-weight: normal;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  .loading ~ span {
    margin-left: 8px;
  }
  span {
    display: flex;
    justify-content: center;
    align-items: center;
    line-height: 22px;
  }
  & ~ .btn {
    margin-left: 15px;
  }
}
.small-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: @white;
  border: 1px solid @Black03;
  width: 164px;
  line-height: 22px;
}
.activate {
  background-color: @white;
  border: 1px solid @Black03;
  color: @Black50;
}
.deactivate {
  background-color: @Black50;
  border: 1px solid @Black50;
  border: none;
  color: @white;
  &.count-down-disabled {
    background: @Black-20;
  }
}
</style>
